import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Plus, Send } from "lucide-react";
import { T, MANROPE, TYPE_ORDER, DUE_GROUPS, dueBucket, weekEndOf, ddMon } from "./todoTokens";
import { SummaryStrip } from "./SummaryStrip";
import { TodoToolbar } from "./TodoToolbar";
import { TodoGroup } from "./TodoGroup";
import { TodoRow } from "./TodoRow";
import { TodoDrawer } from "./TodoDrawer";
import { BulkBar } from "./BulkBar";
import { UndoToast } from "./UndoToast";
import { DueReasonDialog } from "./DueReasonDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const store = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };

export default function TodosPage() {
  const [params, setParams] = useSearchParams();
  const [todos, setTodos] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reps, setReps] = useState([]);
  const [selected, setSelected] = useState([]);
  const [drawer, setDrawer] = useState({ open: false, editing: null });
  const [toast, setToast] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [dueReason, setDueReason] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [density, setDensity] = useState(() => store("equipr-todos-density", "comfortable"));
  const busy = useRef(false);

  // filters live in the URL so a filtered view can be shared (directive §2)
  const get = (k, d) => params.get(k) || d;
  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v && v !== "all" ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const typeFilter = get("type", "all");
  const quick = params.get("quick");
  const assignee = get("assignee", "all");
  const client = get("client", "all");
  const q = get("q", "");
  const group = get("group", store("equipr-todos-group", "due"));
  const showDone = params.get("done") === "1";

  const myName = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("equipr_session") || "{}").name || ""; } catch { return ""; }
  }, []);
  // only Anthony can move a due date without saying why
  const dueReasonExempt = useMemo(() => {
    try { return (JSON.parse(localStorage.getItem("equipr_session") || "{}").id || "") === "anthony"; }
    catch { return false; }
  }, []);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/todos`, { params: { include_done: showDone, limit: 2000 } }),
        axios.get(`${API}/todos/summary`),
      ]);
      setTodos(a.data.todos || []);
      setSummary(b.data);
    } catch { /* keep what is on screen */ } finally { setLoading(false); }
  }, [showDone]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // refreshes must never close an open menu or lose typed text, so they only
    // replace data (directive §13)
    const iv = setInterval(() => { if (!busy.current) load(); }, 60000);
    const onFocus = () => { if (!busy.current) load(); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [load]);
  useEffect(() => {
    axios.get(`${API}/agents/mapping`)
      .then((r) => setReps((r.data.agents || []).filter((x) => x.id !== "emergency").map((x) => x.name).filter(Boolean)))
      .catch(() => {});
  }, []);
  useEffect(() => { busy.current = drawer.open || !!dueReason; }, [drawer.open, dueReason]);

  const today = summary?.today || new Date().toISOString().slice(0, 10);
  const weekEnd = weekEndOf(today);
  const thisYear = new Date().getFullYear();

  const assignees = useMemo(
    () => Array.from(new Set(todos.map((t) => (t.assignee || {}).name).filter(Boolean))).sort(),
    [todos]);
  const clientNames = useMemo(
    () => Array.from(new Set(todos.filter((t) => t.link_type === "client").map((t) => t.link_name).filter(Boolean))).sort(),
    [todos]);
  const clientOptions = useMemo(() => {
    const m = new Map();
    todos.filter((t) => t.link_type === "client" && t.link_id).forEach((t) => m.set(t.link_id, t.link_name || t.link_id));
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [todos]);

  /* One filter chain. Type counts are produced from the set filtered by
     everything EXCEPT type, so the chips stay meaningful (directive §6). */
  const matches = (t, { skipType } = {}) => {
    if (!showDone && t.status === "done") return false;
    if (!skipType && typeFilter !== "all" && t.type !== typeFilter) return false;
    if (quick === "overdue" && !t.overdue) return false;
    if (quick === "due_today" && String(t.due_date || "").slice(0, 10) !== today) return false;
    if (quick === "in_progress" && t.status !== "in_progress") return false;
    if (assignee === "__mine" ? (t.assignee || {}).name !== myName
      : assignee !== "all" && (t.assignee || {}).name !== assignee) return false;
    if (client === "__none" ? t.link_type === "client" : client !== "all" && t.link_name !== client) return false;
    if (q) {
      const hay = [t.title, ...(t.points || []), t.link_name, (t.assignee || {}).name, t.source_label]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  };

  const rows = useMemo(() => todos.filter((t) => matches(t)), [todos, typeFilter, quick, assignee, client, q, showDone, today, myName]);
  const typeCounts = useMemo(() => {
    const base = todos.filter((t) => matches(t, { skipType: true }) && t.status !== "done");
    return TYPE_ORDER.reduce((acc, k) => ({ ...acc, [k]: base.filter((t) => t.type === k).length }), {});
  }, [todos, quick, assignee, client, q, showDone, today, myName]);

  const groups = useMemo(() => buildGroups(rows, group, today, weekEnd, thisYear), [rows, group, today, weekEnd, thisYear]);
  const filtersActive = typeFilter !== "all" || !!quick || assignee !== "all" || client !== "all" || !!q;

  /* ── writes ─────────────────────────────────────────────────────────── */
  const patch = async (t, body, note) => {
    const before = { status: t.status, due_date: t.due_date };
    setTodos((ls) => ls.map((x) => (x.id === t.id ? { ...x, ...body } : x)));
    try {
      await axios.patch(`${API}/todos/${t.id}`, body);
      if (note) setToast({ text: note, undo: async () => { await axios.patch(`${API}/todos/${t.id}`, before); load(); } });
      await load();
    } catch (e) {
      setTodos((ls) => ls.map((x) => (x.id === t.id ? { ...x, ...before } : x)));
      setToast({ text: e?.response?.data?.detail || "That didn't save", retry: () => patch(t, body, note) });
    }
  };

  const cut = (s) => (s || "").length > 48 ? `${s.slice(0, 48)}…` : s;
  const onStatus = (t, status) =>
    patch(t, { status }, status === "done" ? `Marked done: ${cut(t.title)}` : null);
  const onDate = (t, due_date) => {
    const from = (t.due_date || "").slice(0, 10);
    if (!from || dueReasonExempt || from === String(due_date).slice(0, 10))
      return patch(t, { due_date }, null);
    setDueReason({
      from, to: due_date, count: 1,
      onConfirm: async (reason) => {
        setDueReason(null);
        await patch(t, { due_date, due_reason: reason }, null);
      },
    });
  };
  const onComment = async (t, text) => {
    try { await axios.post(`${API}/todos/${t.id}/comment`, { text }); load(); }
    catch (e) { setToast({ text: e?.response?.data?.detail || "Comment failed" }); }
  };
  const onDelete = async (t) => {
    setTodos((ls) => ls.filter((x) => x.id !== t.id));
    try { await axios.delete(`${API}/todos/${t.id}`); setToast({ text: `Deleted: ${cut(t.title)}` }); load(); }
    catch (e) { setToast({ text: e?.response?.data?.detail || "Delete failed" }); load(); }
  };

  const saveDrawer = async (f, due_reason) => {
    const body = {
      points: f.points, title: f.points[0], type: f.type, status: f.status,
      due_date: f.due_date || null,
      owner_name: f.owner || null, owner_names: f.owner ? [f.owner] : [],
      link_type: f.client ? "client" : null, link_id: f.client || null,
      link_name: f.client ? (clientOptions.find((c) => c.id === f.client) || {}).name : null,
    };
    const from = ((drawer.editing || {}).due_date || "").slice(0, 10);
    if (drawer.editing && from && !dueReasonExempt && !due_reason
        && from !== String(f.due_date || "").slice(0, 10)) {
      setDueReason({
        from, to: f.due_date, count: 1,
        onConfirm: async (reason) => { setDueReason(null); await saveDrawer(f, reason); },
      });
      return;
    }
    if (due_reason) body.due_reason = due_reason;
    try {
      if (drawer.editing) await axios.patch(`${API}/todos/${drawer.editing.id}`, body);
      else await axios.post(`${API}/todos`, body);
      setDrawer({ open: false, editing: null });
      setToast({ text: `${drawer.editing ? "Saved" : "Added"}: ${cut(f.points[0])}` });
      load();
    } catch (e) { setToast({ text: e?.response?.data?.detail || "Could not save" }); }
  };

  const bulk = async (action, extra) => {
    if (action === "reschedule" && !dueReasonExempt && !(extra || {}).due_reason
        && todos.some((t) => selected.includes(t.id) && t.due_date
          && t.due_date.slice(0, 10) !== String(extra.due_date).slice(0, 10))) {
      setDueReason({
        from: null, to: extra.due_date, count: selected.length,
        onConfirm: async (reason) => {
          setDueReason(null);
          await bulk(action, { ...extra, due_reason: reason });
        },
      });
      return;
    }
    try {
      const r = await axios.post(`${API}/todos/bulk`, { ids: selected, action, ...extra });
      setToast({ text: `${r.data.changed} to-do${r.data.changed === 1 ? "" : "s"} updated${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}` });
      setSelected([]); load();
    } catch (e) { setToast({ text: e?.response?.data?.detail || "Bulk action failed" }); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await axios.post(`${API}/sales/follow-ups/send-digest`);
      setToast({ text: `Digest sent to ${r.data.sent || 0} rep${r.data.sent === 1 ? "" : "s"}` });
    } catch (e) { setToast({ text: e?.response?.data?.detail || "Digest failed" }); }
    finally { setSendingDigest(false); }
  };

  const todayLabel = `${DAYS[new Date(`${today}T00:00:00`).getDay()]} ${ddMon(today, thisYear)}`;

  return (
    <div className="space-y-4" data-testid="company-todos">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 style={{ ...MANROPE, fontSize: 25, fontWeight: 700, color: T.ink }}>To-dos</h1>
          <p className="text-[14px]" style={{ color: T.ink2 }}>
            Every piece of assigned work in one list: meeting actions, journey tasks, sales follow-ups, health checks and goal check-ins.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0" style={{ whiteSpace: "nowrap" }}>
          <button onClick={() => setDensity((d) => { const n = d === "compact" ? "comfortable" : "compact"; localStorage.setItem("equipr-todos-density", n); return n; })}
            className="h-9 px-3 rounded-[8px] text-[12.5px]" style={{ border: `1px solid ${T.cardEdge}`, color: T.ink2, background: "#fff" }}
            data-testid="todos-density">{density === "compact" ? "Compact" : "Comfortable"}</button>
          <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none" style={{ color: T.ink2 }}>
            <span onClick={() => setParam({ done: showDone ? null : "1" })} data-testid="todos-show-done"
              style={{ width: 34, height: 18, borderRadius: 999, position: "relative", display: "inline-block",
                background: showDone ? T.accentInk : "#C9C3B6", transition: "background 160ms" }}>
              <span style={{ position: "absolute", top: 2, left: showDone ? 18 : 2, width: 14, height: 14, borderRadius: 999,
                background: showDone ? T.accent : "#fff", transition: "left 160ms" }} />
            </span>
            Show done
          </label>
          <button onClick={sendDigest} disabled={sendingDigest} data-testid="company-todos-send-digest"
            className="h-9 px-3 rounded-[8px] text-[12.5px] font-semibold inline-flex items-center gap-1.5"
            style={{ border: `1px solid ${T.cardEdge}`, color: T.ink2, background: "#fff" }}
            title="Emails each rep the sales follow-ups they owe">
            <Send className="w-3.5 h-3.5" /> {sendingDigest ? "Sending…" : "Send digest"}
          </button>
          <button onClick={() => setDrawer({ open: true, editing: null })} data-testid="company-actions-add"
            className="h-9 px-3.5 rounded-[8px] text-[12.5px] font-bold inline-flex items-center gap-1.5"
            style={{ background: T.accent, border: `1px solid ${T.accentEdge}`, color: T.accentInk }}>
            <Plus className="w-3.5 h-3.5" /> Add to-do
          </button>
        </div>
      </div>

      <SummaryStrip summary={summary} quick={quick} todayLabel={todayLabel}
        onQuick={(k) => setParam({ quick: k })} />

      <TodoToolbar
        typeFilter={typeFilter} setTypeFilter={(v) => setParam({ type: v })} typeCounts={typeCounts}
        q={q} setQ={(v) => setParam({ q: v })}
        assignee={assignee} setAssignee={(v) => setParam({ assignee: v })} assignees={assignees}
        client={client} setClient={(v) => setParam({ client: v })} clients={clientNames}
        group={group} setGroup={(v) => { localStorage.setItem("equipr-todos-group", v); setParam({ group: v }); }}
        filtersActive={filtersActive} shown={rows.filter((t) => t.status !== "done").length}
        openTotal={summary?.open ?? 0}
        onClear={() => setParams(new URLSearchParams(showDone ? { done: "1" } : {}), { replace: true })} />

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="rounded-[10px] h-[220px] animate-pulse"
            style={{ background: T.card, border: `1px solid ${T.cardEdge}` }} />)}
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-[10px] p-10 text-center" style={{ background: T.card, border: `1px solid ${T.cardEdge}` }}
          data-testid="todos-empty">
          <div className="text-[14px] font-semibold" style={{ color: T.ink }}>
            {filtersActive ? "No to-dos match these filters." : "No to-dos yet."}
          </div>
          {!filtersActive && (
            <button onClick={() => setDrawer({ open: true, editing: null })} data-testid="todos-empty-add"
              className="mt-3 h-9 px-3.5 rounded-[8px] text-[12.5px] font-bold inline-flex items-center gap-1.5"
              style={{ background: T.accent, border: `1px solid ${T.accentEdge}`, color: T.accentInk }}>
              <Plus className="w-3.5 h-3.5" /> Add to-do
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <TodoGroup key={g.key} g={g} collapsed={!!collapsed[`${group}:${g.key}`]}
              onToggle={() => setCollapsed((c) => ({ ...c, [`${group}:${g.key}`]: !c[`${group}:${g.key}`] }))}>
              {g.items.map((t) => (
                <TodoRow key={t.id} t={t} today={today} thisYear={thisYear} myName={myName} density={density}
                  selected={selected.includes(t.id)}
                  onSelect={(id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])}
                  onStatus={onStatus} onDate={onDate} onComment={onComment}
                  onEdit={(x) => setDrawer({ open: true, editing: x })} onDelete={onDelete}
                  onRecordCheck={(x) => { window.location.href = `/health-check/${x.link_id}/staff`; }} />
              ))}
            </TodoGroup>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <BulkBar count={selected.length} reps={reps} onClear={() => setSelected([])} onAction={bulk} />
      )}

      <TodoDrawer open={drawer.open} editing={drawer.editing} reps={reps} clients={clientOptions} myName={myName}
        onClose={() => setDrawer({ open: false, editing: null })} onSave={saveDrawer} />

      {dueReason && (
        <DueReasonDialog {...dueReason} onCancel={() => { setDueReason(null); load(); }} />
      )}

      {toast && <UndoToast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

/* Directive §8 — groups are worked out on every render, never stored. */
function buildGroups(rows, mode, today, weekEnd, thisYear) {
  const bucket = (t) => {
    if (mode === "assignee") return (t.assignee || {}).name || "Unassigned";
    if (mode === "client") return t.link_type === "client" ? (t.link_name || t.link_id) : "No client";
    return dueBucket(t.due_date, today, weekEnd);
  };
  const map = new Map();
  rows.forEach((t) => {
    const k = bucket(t);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(t);
  });
  const sortItems = (list) => list.slice().sort((a, b) =>
    (a.due_date || "9999").localeCompare(b.due_date || "9999") || (a.created_at || "").localeCompare(b.created_at || ""));

  const wrap = (key, label, items, extra = {}) => ({
    key, label, mode, items: sortItems(items),
    openCount: items.filter((t) => t.status !== "done").length,
    overdueCount: items.filter((t) => t.overdue && t.status !== "done").length,
    ...extra,
  });

  if (mode === "due") {
    const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const subs = {
      due_today: `${DAYS[new Date(`${today}T00:00:00`).getDay()]} ${ddMon(today, thisYear)}`,
      this_week: `to ${DAYS[new Date(`${weekEnd}T00:00:00`).getDay()]} ${ddMon(weekEnd, thisYear)}`,
    };
    return DUE_GROUPS.filter((g) => (map.get(g.key) || []).length)
      .map((g) => wrap(g.key, g.label, map.get(g.key), { tone: g.tone, sub: subs[g.key] }));
  }

  const out = Array.from(map, ([k, items]) => wrap(k, k, items));
  const last = mode === "assignee" ? "Unassigned" : "No client";
  out.sort((a, b) => {
    if (a.key === last) return 1;
    if (b.key === last) return -1;
    if (mode === "assignee") return b.overdueCount - a.overdueCount || b.items.length - a.items.length;
    return a.label.localeCompare(b.label);
  });
  return out;
}
